/**
 * SignUpContainer
 * --
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import SignUpPresenter from './SignUpPresenter';
import { useDuplicateCheck, useVerify } from 'hooks/AuthHooks';
import { useCreateUser } from 'hooks/UserHooks';
import MessageAlert, { DEPLOY_TYPE, DOMAIN_URL } from 'utils';
import validateCheck, { passwordCheck } from 'utils/Validation';
import Formatter from 'utils/Formatter';
import { openAuthPopup } from 'utils/NiceAPIManager';
import { decrypt } from 'utils/Crypto';
import { useSendEmail } from 'hooks/SendHooks';
import { LoadingPage } from 'components';
import { FileManager } from 'utils/FileManager';

const SignUpContainer = (props) => {
  /* ===== PROPS ===== */
  const { navigate, screenSize, searchParams } = props;

  /* ===== ROUTE ===== */
  const type = searchParams.get('type');
  const social = searchParams.get('social');
  const biz = searchParams.get('biz');

  /* ===== Mutate ===== */
  // 중복검사
  const { duplicate } = useDuplicateCheck();
  // 회원가입
  const { createUser } = useCreateUser();
  // 이메일 인증
  const { sendEmail } = useSendEmail();

  /* ===== STATE ===== */
  const inputRef = useRef({});
  // const authCodeRef = useRef(null);
  const timerStart = useRef(null);

  // Input 입력값
  const [inputValue, setInputValue] = useState({});
  const [status, setStatus] = useState({});
  const [message, setMessage] = useState({});
  // Checkbox 입력값
  const [checkedAll, setCheckedAll] = useState(false);
  const [checkedList, setCheckedList] = useState({
    terms_of_use: false, // 이용약관 동의
    terms_of_privacy: false, // 개인정보 수집 및 이용 동의
    terms_of_marketing: false, // 마케팅 목적의 개인정보 수집 및 이용 동의
    terms_of_ad: false, // 혜택 및 광고성 정보 수신 동의
    terms_of_age: false, // 만 14세 이상입니다
    marketing_consent: false, // 마케팅정보 수집 및 활용 동의
  });
  // 중복검사 실행여부
  const [isDuplicate, setIsDuplicate] = useState({
    user_account: false,
    user_email: false,
  });
  // 본인인증 여부
  const [isAuthCheck, setIsAuthCheck] = useState(false);
  // 인증 진행중인지 체크
  const [isAuthProgress, setIsAuthProgress] = useState(false);
  // 나이스 인증 EncodeData
  const [encodeData, setEncodeData] = useState('');
  // 이메일 인증번호
  const [inputAuthCode, setInputAuthCode] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [timeText, setTimeText] = useState('');
  const [authStatus, setAuthStatus] = useState('');
  // 로딩
  const [isLoading, setIsLoading] = useState(false);
  // 사업자 회원가입 여부
  const [isBiz, setIsBiz] = useState(false);
  // 사업자 정보
  const [bizInfo, setBizInfo] = useState({
    business_num: '',
    business_num_url: '',
    business_level: 0,
  });

  /* ===== Data ===== */
  // 나이스 사용자 정보 조회
  const { data: verify } = useVerify(
    encodeData && {
      EncodeData: encodeURIComponent(encodeData),
    }
  );

  /* ===== VARIABLE ===== */
  // 체크박스 아이템
  const checkListItem = useMemo(() => {
    const items = [
      {
        key: 'terms_of_use',
        label: '이용약관 동의',
        value: '이용약관 동의',
        required: true,
        url: '',
      },
      {
        key: 'terms_of_privacy',
        label: '개인정보 수집 및 이용 동의',
        value: '개인정보 수집 및 이용 동의',
        required: true,
        url: '',
      },
      {
        key: 'terms_of_marketing',
        label: '마케팅 목적의 개인정보 수집 및 이용 동의',
        value: '마케팅 목적의 개인정보 수집 및 이용 동의',
        url: '',
      },
      {
        key: 'terms_of_ad',
        label: '혜택 및 광고성 정보 수신 동의',
        value: '혜택 및 광고성 정보 수신 동의',
        url: '',
      },
      {
        key: 'terms_of_age',
        label: '만 14세 이상입니다',
        value: '만 14세 이상입니다',
        required: true,
        url: '',
      },
    ];

    return items;
  }, []);

  // 유효성 검사 메세지
  const inputMessage = {
    user_account: {
      empty: '아이디를 입력해주세요.',
      check: '아이디 중복확인을 해주세요.',
    },
    password: {
      empty: '비밀번호를 입력해주세요.',
      discord: '비밀번호가 일치하지 않습니다.',
    },
    re_password: {
      empty: '비밀번호를 한번 더 입력해주세요.',
    },
    user_name: {
      empty: '이름을 입력해주세요.',
    },
    // user_email: {
    //   empty: '이메일을 입력해주세요.',
    //   check: '이메일 중복검사를 해주세요.',
    // },
    user_phone: {
      empty: '휴대폰 번호를 입력해주세요.',
      auth: '휴대폰 인증을 해주세요.',
    },
    postcode: {
      empty: '우편번호를 입력해주세요.',
    },
    user_address: {
      empty: '주소를 입력해주세요.',
    },
  };

  /* ===== FUNCTION ===== */
  /**
   * 회원가입
   * --
   */
  const handleSubmit = async () => {
    if (!handleValidateCheck()) return;

    // 사업자 회원 유효성 검사
    if (isBiz) {
      const { business_num, business_num_url } = bizInfo;

      if (!business_num) {
        MessageAlert.info('사업자번호를 입력해주세요!');
        return;
      }

      if (business_num.length < 12) {
        MessageAlert.info(
          '유효하지 않은 사업자번호입니다. 사업자번호를 확인해주세요!'
        );
        return;
      }

      if (!business_num_url) {
        MessageAlert.info('사업자 등록증을 업로드해주세요!');
        return;
      }
    }

    try {
      const { year, month, day, ...d } = inputValue;
      const birthday = new Date(year, month - 1, day);

      let filUrl = null;

      if (isBiz) {
        const { status: uploadStatus, data: uploadData } =
          await FileManager.upload(`users/business`, bizInfo.business_num_url);

        if (uploadStatus !== 200) {
          MessageAlert.info('파일 업로드에 실패하였습니다.');
          return;
        }

        filUrl = uploadData?.file_name;
      }

      let userData = {
        ...d,
        user_birthday: Formatter.dateToUnix(birthday),
        user_setting: {
          ...checkedList,
          location_consent: false,
          push_notification: false,
          event_notification: false,
        },
        ...bizInfo,
        business_num_url: filUrl,
        business_level: isBiz ? 1 : 0,
      };

      // NOTE: 소셜 회원가입 체크
      if (social) {
        const socialData = JSON.parse(decrypt(decodeURIComponent(social)));

        userData = {
          ...userData,
          ...socialData,
        };
      }

      const { status } = await createUser(userData);

      if (status !== 200) {
        MessageAlert.warning('회원가입 중 오류가 발생하였습니다!');
        console.error('[CREATE USER] ERROR : ', status);
        return;
      }

      MessageAlert.success('회원가입되었습니다');
      if (type && type === 'mobile') {
        // 모바일
        navigate(`/components/signup/success${type ? `?type=${type}` : ''}`);
      } else {
        // PC
        navigate(`/signup/success${type ? `?type=${type}` : ''}`);
      }
    } catch (err) {
      console.error('[CREATE USER] ERROR : ', err);
      MessageAlert.error('회원가입 중 오류가 발생하였습니다!');
      throw err;
    }
  };

  /**
   * 중복검사
   * --
   * @param {string} t
   * @param {string | number} v
   */
  const handleDuplicate = async (t, v) => {
    // 빈값 유효성 검사
    if (t !== 'user_email' && !v) {
      inputRef[t]?.focus();
      setStatus({
        [t]: 'error',
      });
      setMessage({
        [t]: inputMessage[t].empty,
      });

      return false;
    } else {
      setStatus({});
      setMessage({});
    }

    if (!social) {
      // 아이디 유효성 검사
      if (t === 'user_account') {
        // 아이디 길이 검사
        if (v.length < 4 || v.length > 20) {
          const message = '아이디는 4자리 이상 20자리 이하로 입력해주세요.';
          MessageAlert.warning(message);
          setStatus({
            [t]: 'error',
          });
          setMessage({
            [t]: message,
          });
          inputRef[t]?.focus();
          return false;
        }

        // 아이디형식 검사
        if (!validateCheck('account', v)) {
          const message =
            '아이디는 하나 이상의 영문자, 숫자가 포함되어야합니다.';
          MessageAlert.warning(message);
          setStatus({
            [t]: 'error',
          });
          setMessage({
            [t]: message,
          });
          MessageAlert.warning(message);
          inputRef[t]?.focus();
          return false;
        }
      }

      // 이메일 유효성 검사
      if (t === 'user_email') {
        if (!validateCheck('email', v)) {
          const message = '이메일 형식이 아닙니다.';
          MessageAlert.warning(message);
          setStatus({
            [t]: 'error',
          });
          setMessage({
            [t]: message,
          });
          MessageAlert.warning(message);
          inputRef[t]?.focus();
          return false;
        }
      }
    }

    try {
      const duplicateData = {
        [t]: v,
        type: 'USER',
      };

      const type = {
        user_account: '아이디',
        user_email: '이메일',
      };

      const { data } = await duplicate(duplicateData);

      if (!data) {
        MessageAlert.warning(`사용할 수 없는 ${type[t]}입니다.`);
        return false;
      } else {
        const confirm = window.confirm(
          `사용가능한 ${type[t]}입니다. 사용하시겠습니까?`
        );

        if (!confirm) return false;

        setIsDuplicate((prev) => ({
          ...prev,
          [t]: true,
        }));

        return true;
      }
    } catch (err) {
      console.error('[Duplicate Check] ERROR : ', err);
      MessageAlert.error('중복 확인 중 오류가 발생하였습니다!');
      return false;
    }
  };

  /**
   * 유효성 검사
   * --
   */
  const handleValidateCheck = () => {
    // NOTE: 필수 입력 값 중 빈 값이 있는지 체크
    const inputArr = Object.keys(inputRef).reduce((acc, cur) => {
      if (cur === 'current' || cur === 'user_email') return acc;

      acc.push(cur);
      return acc;
    }, []);

    for (let i in inputArr) {
      const key = inputArr[i];
      if (!inputRef[key]?.input.value) {
        inputRef[key]?.focus();
        setStatus({
          [key]: 'error',
        });
        setMessage({
          [key]: inputMessage[key].empty,
        });

        return false;
      }
      setStatus({});
      setMessage({});
    }

    if (!social) {
      // NOTE: 비밀번호 유효성 검사
      const { password, re_password } = inputValue;
      if (!passwordCheck(password)) {
        inputRef['password'].focus();
        return false;
      }

      if (password !== re_password) {
        setStatus({
          password: 'error',
        });
        setMessage({
          password: inputMessage['password'].discord,
        });
        inputRef['password'].focus();
        return false;
      }
    }

    // NOTE: 중복검사 여부 체크
    if (!social) {
      const duplicateArr = Object.keys(isDuplicate).reduce((acc, cur) => {
        if (cur === 'user_email') return acc;
        acc.push(cur);
        return acc;
      }, []);

      for (let i in duplicateArr) {
        const key = duplicateArr[i];

        if (!isDuplicate[key]) {
          inputRef[key]?.focus();
          setStatus({
            [key]: 'error',
          });
          setMessage({
            [key]: inputMessage[key].check,
          });
          return false;
        }
        setStatus({});
        setMessage({});
      }
    }

    // NOTE: 휴대폰 인증 여부 체크
    if (!isAuthCheck) {
      inputRef['user_phone'].focus();
      setStatus({
        user_phone: 'error',
      });
      setMessage({
        user_phone: inputMessage['user_phone'].auth,
      });
      return false;
    }

    // NOTE: 필수 동의 여부 체크
    const checkRequired = checkListItem.some(
      (_item) => _item.required && !checkedList[_item.key]
    );
    if (checkRequired) {
      MessageAlert.warning('필수 약관에 모두 동의해주세요.');
      return false;
    }

    return true;
  };

  /**
   * 데이터 바인딩 함수
   * --
   */
  const handleChange = (t, v, callback = setInputValue) => {
    callback((prevState) => ({
      ...prevState,
      [t]: v,
    }));
  };

  /**
   * 전체 선택
   * --
   */
  const handleCheckedAll = (c) => {
    const checkedObj = Object.entries(checkedList).reduce(
      (acc, [key, _]) => ({
        ...acc,
        [key]: c,
      }),
      {}
    );

    setCheckedAll(c);
    setCheckedList(checkedObj);
  };

  /**
   * 체크박스 선택
   * --
   */
  const handleChecked = (t, c) => {
    setCheckedList((prev) => ({
      ...prev,
      [t]: c,
    }));
  };

  /**
   * 나이스 본인인증
   * --
   */
  const handleOpenAuthModal = () => {
    // 나이스 본인인증 팝업 열기
    openAuthPopup();
    // 나이스 본인인증 진행중 표시
    setIsAuthProgress(true);
  };

  /**
   * 인증번호 전송
   * --
   */
  const handleSendAuthCode = async () => {
    const { user_email } = inputValue;

    // 이메일 유효성 검사
    if (!(await handleDuplicate('user_email', user_email))) {
      return;
    }

    try {
      setIsLoading(true);

      // 인증번호 전송
      const bodyData = {
        to_email: user_email,
        type: 'CODE',
        user_type: 'USER',
        is_template: 'Y',
      };

      const { status, data } = await sendEmail(bodyData);

      if (status !== 200) {
        MessageAlert.warning('인증번호 전송에 실패하였습니다.');
        return;
      }

      setAuthCode(data.code);
      handleStartTimer();
    } catch (err) {
      console.log(`인증번호 전송 실패`, err);
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /**
   * 인증코드 입력 시간 제한 설정
   * --
   */
  const handleStartTimer = () => {
    if (timerStart.current) {
      clearInterval(timerStart.current);
      timerStart.current = null;
    }

    let time = 300;
    timerStart.current = setInterval(() => {
      --time;

      if (time === 0) {
        setAuthCode('만료');
        clearInterval(timerStart.current);
        timerStart.current = null;
        setTimeText('시간만료');
        return;
      }

      const minutes = Math.floor(time / 60);
      const seconds = time % 60;
      const formattedMinutes = String(minutes).padStart(2, '0');
      const formattedSeconds = String(seconds).padStart(2, '0');
      setTimeText(`${formattedMinutes}:${formattedSeconds}`);
    }, 1000);
  };

  /**
   * 인증코드 체크
   * --
   */
  const handleAuthCheck = () => {
    if (authCode === '만료') {
      inputRef['inputAuthCode'].focus();
      setStatus({
        inputAuthCode: 'error',
      });
      setMessage({
        inputAuthCode: '인증코드가 만료되었습니다. 다시 인증해주세요!',
      });
      return;
    }

    if (!inputAuthCode || authCode !== inputAuthCode) {
      setStatus({
        inputAuthCode: 'error',
      });
      setMessage({
        inputAuthCode: '인증번호가 올바르지 않습니다.',
      });
      return;
    }

    setStatus({});
    setMessage({});
    setAuthStatus('success');
    setTimeText('');

    clearInterval(timerStart.current);
    timerStart.current = null;
  };

  /**
   * 휴대폰 번호 중복검사
   * --
   * @param {string} phone
   * @returns
   */
  const handlePhoneDuplicateCheck = async (phone) => {
    try {
      const { status, data } = await duplicate({
        type: 'USER',
        user_phone: phone,
      });

      if (status !== 200) {
        MessageAlert.warning('본인인증에 실패하였습니다.');
        return false;
      }

      return data;
    } catch (err) {
      console.error('[handlePhoneDuplicateCheck] Error!! ', err);
      throw err;
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 전체 선택 감지
   * --
   */
  useEffect(() => {
    const isAllCheck = Object.entries(checkedList).some(
      ([key, value]) => value === false
    );

    if (isAllCheck) {
      setCheckedAll(false);
    } else {
      setCheckedAll(true);
    }
  }, [checkedList, checkListItem]);

  /**
   * 본인인증 팝업 메세지 감지
   * --
   */
  useEffect(() => {
    const port = DEPLOY_TYPE === 'local' ? ':3000' : '';
    const handleMessage = (event) => {
      const domain =
        DEPLOY_TYPE === 'development'
          ? `mall-jm-coffee.${DOMAIN_URL}`
          : `mall.${DOMAIN_URL}`;

      // 이벤트의 출처를 확인하여 신뢰할 수 있는 출처에서 온 메시지만 처리
      if (
        event.origin === `${window.location.protocol}//${DOMAIN_URL}${port}` ||
        event.origin === `${window.location.protocol}//${domain}${port}`
      ) {
        const data = event.data;

        // AUTH 타입의 메시지인 경우
        if (data && data.type === 'AUTH') {
          setEncodeData(data.data); // 데이터를 상태로 설정
          setIsAuthProgress(false); // 인증 진행 상태를 false로 설정
        }
      }
    };

    // 인증 진행 중인 경우에만 메시지 리스너를 등록
    if (isAuthProgress) {
      window.addEventListener('message', handleMessage);
    } else {
      window.removeEventListener('message', handleMessage);
    }

    return () => {
      // 컴포넌트가 언마운트될 때 메시지 리스너를 제거
      window.removeEventListener('message', handleMessage);
    };
  }, [isAuthProgress]);

  /**
   * 본인인증 데이터 디코딩
   * --
   */
  useEffect(() => {
    const check = async (phone, callback) => {
      const res = await handlePhoneDuplicateCheck(phone);

      if (!res) {
        MessageAlert.warning('사용할 수 없는 휴대폰번호입니다.');
        return;
      }

      return callback();
    };

    if (verify) {
      const { status, data } = verify;

      if (status === 200) {
        const { name, gender, mobileno, birthdate } = data;

        const phoneValue = Formatter.hyphen(mobileno);

        const GENDER = {
          0: 'F',
          1: 'M',
        };

        const call = () => {
          handleChange('user_name', name);
          handleChange('user_phone', phoneValue);
          handleChange('user_gender', GENDER[gender]);
          handleChange('year', birthdate.slice(0, 4));
          handleChange('month', birthdate.slice(4, 6));
          handleChange('day', birthdate.slice(6, 8));

          setIsAuthCheck(true);
        };

        check(phoneValue, call);
      }
    }
    // eslint-disable-next-line
  }, [verify]);

  /**
   * 사업자 회원 여부 체크
   * --
   */
  useEffect(() => {
    if (!biz) return setIsBiz(false);

    if (biz === 'partner') setIsBiz(true);
    else setIsBiz(false);
  }, [biz]);

  /* ===== RENDER ===== */
  return (
    <>
      <SignUpPresenter
        inputRef={inputRef}
        inputValue={inputValue}
        message={message}
        status={status}
        screenSize={screenSize}
        checkedList={checkedList}
        checkedAll={checkedAll}
        checkListItem={checkListItem}
        isDuplicate={isDuplicate}
        isAuthCheck={isAuthCheck}
        authCode={authCode}
        timeText={timeText}
        authStatus={authStatus}
        inputAuthCode={inputAuthCode}
        isBiz={isBiz}
        bizInfo={bizInfo}
        setBizInfo={setBizInfo}
        onChange={handleChange}
        onSubmit={handleSubmit}
        navigate={navigate}
        onChecked={handleChecked}
        onCheckedAll={handleCheckedAll}
        onDuplicate={handleDuplicate}
        onOpen={handleOpenAuthModal}
        onSendAuthCode={handleSendAuthCode}
        onAuthCode={handleAuthCheck}
        setInputAuthCode={setInputAuthCode}
        social={social}
      />
      {isLoading ? (
        <LoadingPage text={'인증코드를 전송중입니다. 잠시만 기다려주세요.'} />
      ) : null}
    </>
  );
};

export default SignUpContainer;
